import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Chip, List, ListItem, ListItemText, Divider, Tabs, Tab, Button } from '@mui/material';
import { Assignment as MissionIcon, CheckCircle as ActiveIcon, Explore as AvailableIcon, PlayArrow as AcceptIcon } from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import { useChatContext } from '../../contexts/ChatContext';
import { useConsole } from '../../contexts/ConsoleContext';
import type { Mission } from '../../types/gameState';

interface MissionsPanelProps {
  playerId: string;
}

type TabType = 'active' | 'available';

export function MissionsPanel({ playerId }: MissionsPanelProps) {
  const [activeMissions, setActiveMissions] = useState<Mission[]>([]);
  const [availableMissions, setAvailableMissions] = useState<Mission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<TabType>('active');
  const { sendMessage } = useChatContext();
  const { setSelectedPanel } = useConsole();

  useEffect(() => {
    async function fetchMissions() {
      setIsLoading(true);
      setError(null);

      try {
        // First get player to determine current station
        const playerLogId = logService.api('GET', `/api/players/${playerId}`, {}, 'pending');
        const playerStart = Date.now();
        const playerRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}`);
        const playerDuration = Date.now() - playerStart;

        if (!playerRes.ok) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: `HTTP ${playerRes.status}` });
          setError('Failed to load player data');
          setIsLoading(false);
          return;
        }

        const playerResult = await playerRes.json();
        if (!playerResult.success || !playerResult.data) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: 'No player data' });
          setError('No player data available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(playerLogId, { status: 'success', duration: playerDuration });
        const player = playerResult.data;

        // Fetch player's active missions
        const activeLogId = logService.api('GET', `/api/missions/player/${playerId}`, {}, 'pending');
        const activeStart = Date.now();
        const activeResponse = await fetch(`${DATABASE_SERVICE_URL()}/api/missions/player/${playerId}`);
        const activeDuration = Date.now() - activeStart;

        if (!activeResponse.ok) {
          logService.updateLog(activeLogId, { status: 'error', duration: activeDuration, message: `HTTP ${activeResponse.status}` });
        } else {
          const activeResult = await activeResponse.json();
          if (activeResult.success && activeResult.data) {
            logService.updateLog(activeLogId, { status: 'success', duration: activeDuration });
            setActiveMissions(activeResult.data);
          } else {
            logService.updateLog(activeLogId, { status: 'error', duration: activeDuration, message: 'No active missions data' });
          }
        }

        // Fetch available missions at current station (if docked)
        if (player.current_station_id) {
          const availableLogId = logService.api('GET', `/api/missions/available/${player.current_station_id}`, {}, 'pending');
          const availableStart = Date.now();
          const availableResponse = await fetch(`${DATABASE_SERVICE_URL()}/api/missions/available/${player.current_station_id}`);
          const availableDuration = Date.now() - availableStart;

          if (!availableResponse.ok) {
            logService.updateLog(availableLogId, { status: 'error', duration: availableDuration, message: `HTTP ${availableResponse.status}` });
          } else {
            const availableResult = await availableResponse.json();
            if (availableResult.success && availableResult.data) {
              logService.updateLog(availableLogId, { status: 'success', duration: availableDuration });
              setAvailableMissions(availableResult.data);
            } else {
              logService.updateLog(availableLogId, { status: 'error', duration: availableDuration, message: 'No available missions data' });
            }
          }
        } else {
          // Not docked, no available missions
          setAvailableMissions([]);
        }
      } catch (err) {
        setError('Failed to load missions');
      } finally {
        setIsLoading(false);
      }
    }

    fetchMissions();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="rectangular" height={150} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const missions = currentTab === 'active' ? activeMissions : availableMissions;

  const handleAcceptMission = (mission: Mission) => {
    setSelectedPanel('chat');
    sendMessage(`Please accept the mission "${mission.title}"`);
  };

  return (
    <Box className="panel">
      <Box className="panel-scroll">
        <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
              Missions
            </Typography>

            {/* Tabs */}
            <Tabs value={currentTab} onChange={(_, value) => setCurrentTab(value)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tab
                value="active"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ActiveIcon sx={{ fontSize: 16 }} />
                    <span>Active ({activeMissions.length})</span>
                  </Box>
                }
                sx={{ minHeight: 36 }}
              />
              <Tab
                value="available"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AvailableIcon sx={{ fontSize: 16 }} />
                    <span>Available ({availableMissions.length})</span>
                  </Box>
                }
                sx={{ minHeight: 36 }}
              />
            </Tabs>

            {/* Mission List */}
            {missions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                {currentTab === 'active' ? 'No active missions' : 'No missions available'}
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {missions.map((mission: Mission, index: number) => (
                  <Box key={mission.id}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ px: 1, py: 1.5, alignItems: 'flex-start', display: 'flex' }}>
                      <Box sx={{ color: 'primary.main', mr: 1, mt: 0.5 }}>
                        <MissionIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {mission.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            {mission.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {mission.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Chip label={mission.mission_type.replace('_', ' ')} size="small" color="info" sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }} />
                              <Chip label={`¤${mission.reward_credits.toLocaleString()}`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              {mission.reward_reputation > 0 && (
                                <Chip label={`+${mission.reward_reputation} Rep`} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                              {mission.deadline && (
                                <Chip label={`Due: ${new Date(mission.deadline).toLocaleDateString()}`} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      {currentTab === 'available' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<AcceptIcon />}
                          onClick={() => handleAcceptMission(mission)}
                          sx={{ ml: 1, alignSelf: 'center', minWidth: 80, fontSize: '0.7rem' }}
                        >
                          Accept
                        </Button>
                      )}
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
